import React, { useEffect, useMemo, useState } from "react";
import { Printer, Download, Mail, Trophy, Loader2 } from "lucide-react";
import api, { API } from "../lib/api";
import { useAuth, useCurrency } from "../lib/contexts";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { TeamActivityCard } from "../components/activity/MyActivityCard";
import { toast } from "sonner";

export default function Reports() {
  const { user } = useAuth();
  const { format } = useCurrency();
  const [period, setPeriod] = useState("month");
  const [staffId, setStaffId] = useState("");
  const [users, setUsers] = useState([]);
  const [data, setData] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [sending, setSending] = useState(false);
  const isManager = user?.role === "manager";
  const canDownload = isManager || user?.role === "unit_head";

  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);
  useEffect(() => { api.get("/reports/leaderboard", { params: { period } }).then((r) => setData(r.data)); }, [period]);

  const downloadPdf = () => {
    if (!canDownload) return;
    const t = localStorage.getItem("token");
    const q = new URLSearchParams({ period }); if (staffId) q.set("staff_id", staffId);
    fetch(`${API}/reports/pdf?${q}`, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b); const a = document.createElement("a");
        a.href = url; a.download = `sales_report_${period}.pdf`; a.click();
      });
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const res = await api.post("/reports/email", { period, staff_id: staffId || null, recipient: recipient || null });
      toast.success(`Report emailed to ${res.data.recipient}`);
      setEmailOpen(false); setRecipient("");
    } catch (err) { toast.error(err.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const filteredRows = staffId ? (data?.rows || []).filter((r) => r.user_id === staffId) : (data?.rows || []);
  // Only sales staff book deals, so only they can be reported on.
  const staffOptions = useMemo(() => (users || []).filter((u) => u.role === "staff"), [users]);
  const totalActual = filteredRows.reduce((a, r) => a + (r.total_actual || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 no-print">
        <div>
          <h1 className="font-heading text-3xl font-bold">Sales Performance Report</h1>
          <p className="text-sm text-muted-foreground">Actual value of Won deals — filter by period and staff</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => window.print()} data-testid="print-report"><Printer className="w-4 h-4 mr-2" />Print</Button>
          {canDownload && (
            <Button variant="outline" onClick={downloadPdf} data-testid="pdf-report"><Download className="w-4 h-4 mr-2" />Download PDF</Button>
          )}
          {isManager && (
            <Button onClick={() => setEmailOpen(true)} data-testid="email-report" className="bg-primary hover:bg-primary/90 text-white">
              <Mail className="w-4 h-4 mr-2" />Email Report
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-md border border-border p-3 flex flex-wrap gap-3 no-print">
        <div className="flex bg-neutral-100 rounded-md p-0.5">
          {[["month", "This Month"], ["quarter", "This Quarter"], ["year", "This Year"]].map(([p, l]) => (
            <button key={p} data-testid={`period-${p}`} onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 text-xs font-semibold rounded ${period === p ? "bg-white text-primary shadow-sm" : "text-neutral-500 hover:text-neutral-800"}`}>{l}</button>
          ))}
        </div>
        <select data-testid="report-staff" value={staffId} onChange={(e) => setStaffId(e.target.value)} className="h-9 rounded-md border border-input bg-white px-3 text-sm">
          <option value="">All sales staff</option>
          {staffOptions.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>

      <div className="print-only">
        <h1 className="font-heading text-2xl font-bold text-primary">Sun Nutrafoods — Sales Performance Report</h1>
        <div className="text-sm text-muted-foreground">Period: {period.toUpperCase()} · Generated {new Date().toLocaleString()}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-md border border-border p-5">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Total Actual Value</div>
          <div className="mt-2 font-heading text-3xl font-bold text-primary">{format(totalActual)}</div>
        </div>
        <div className="bg-white rounded-md border border-border p-5">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Deals Won</div>
          <div className="mt-2 font-heading text-3xl font-bold">{filteredRows.reduce((a, r) => a + r.won_count, 0)}</div>
        </div>
        <div className="bg-white rounded-md border border-border p-5">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Active Staff Contributing</div>
          <div className="mt-2 font-heading text-3xl font-bold">{filteredRows.filter((r) => r.won_count > 0).length}</div>
        </div>
      </div>

      <div className="bg-white rounded-md border border-border overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <Trophy className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-semibold">Leaderboard</h3>
        </div>
        {!data ? <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-primary" /></div> : (
          <table className="w-full text-sm" data-testid="leaderboard">
            <thead className="bg-secondary/60 text-[11px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left px-5 py-3 w-12">#</th>
                <th className="text-left px-5 py-3">Sales Staff</th>
                <th className="text-left px-5 py-3">Role</th>
                <th className="text-right px-5 py-3">Deals Won</th>
                <th className="text-right px-5 py-3">Total Actual Value</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r, i) => (
                <tr key={r.user_id} className="border-t border-border">
                  <td className="px-5 py-3">
                    {i === 0 && r.won_count > 0 ? <Trophy className="w-4 h-4 text-amber-500" /> : <span className="text-muted-foreground">{i + 1}</span>}
                  </td>
                  <td className="px-5 py-3 font-semibold">{r.name}</td>
                  <td className="px-5 py-3 text-xs uppercase tracking-widest text-muted-foreground">{r.role}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{r.won_count}</td>
                  <td className="px-5 py-3 text-right tabular-nums font-bold text-primary">{format(r.total_actual)}</td>
                </tr>
              ))}
              {filteredRows.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-sm text-muted-foreground">No records for this period.</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      {isManager && (
        <div className="no-print">
          <Button variant="outline" onClick={() => {
            const t = localStorage.getItem("token");
            fetch(`${API}/export/leaderboard.csv?period=${period}`, { headers: { Authorization: `Bearer ${t}` } })
              .then((r) => r.blob()).then((b) => {
                const url = URL.createObjectURL(b); const a = document.createElement("a");
                a.href = url; a.download = `leaderboard_${period}.csv`; a.click();
              });
          }}><Download className="w-4 h-4 mr-2" />Export Leaderboard CSV</Button>
        </div>
      )}

      {isManager && <TeamActivityCard />}

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Email Report</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">The PDF report will be emailed as an attachment. Recipient must be an existing team member.</p>
            <div>
              <Label>Recipient email (leave blank to send to yourself)</Label>
              <Input type="email" data-testid="email-recipient" placeholder={user?.email} value={recipient} onChange={(e) => setRecipient(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
              <Button data-testid="email-send" onClick={sendEmail} disabled={sending} className="bg-primary hover:bg-primary/90 text-white">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
